import type { AgentCard, AgentStatus, SortMode } from '../types';
import { agentType, cardStatus } from './agentStatus';

const SEVERITY: Record<AgentStatus, number> = { crit: 0, warn: 1, ok: 2, idle: 3 };

const redFlagRank = (card: AgentCard): number => {
  const status = cardStatus(card);
  return status === 'crit' || status === 'warn' ? 0 : 1;
};

const typeRank = (card: AgentCard): number => {
  const kind = agentType(card);
  return kind === 'planned' ? 2 : kind === 'ai' ? 0 : 1;
};

// idle and ok share the last severity tier in the default order
const severityRank = (card: AgentCard): number => Math.min(SEVERITY[cardStatus(card)], 2);

/** Default section order: red flags → AI agents → severity → original order. */
export const defaultOrder = (cards: AgentCard[]): AgentCard[] => {
  const original = new Map(cards.map((c, i) => [c.id, i]));
  return [...cards].sort(
    (a, b) =>
      redFlagRank(a) - redFlagRank(b) ||
      typeRank(a) - typeRank(b) ||
      severityRank(a) - severityRank(b) ||
      (original.get(a.id) ?? 0) - (original.get(b.id) ?? 0),
  );
};

const COMPARATORS: Record<Exclude<SortMode, 'manual'>, (a: AgentCard, b: AgentCard) => number> = {
  name: (a, b) => a.name.localeCompare(b.name),
  runs: (a, b) => b.runs - a.runs,
  status: (a, b) => SEVERITY[cardStatus(a)] - SEVERITY[cardStatus(b)],
};

/** Applies the toolbar sort; 'manual' keeps the caller's (drag) order. */
export const sortCards = (cards: AgentCard[], mode: SortMode): AgentCard[] =>
  mode === 'manual' ? cards : [...cards].sort(COMPARATORS[mode]);
